import { AppStrings } from "../../../../utils/constants/AppStrings";

export type ShareableColumnId =
  | "referralCode"
  | "totalUsers"
  | "totalFtds"
  | "ftdRate"
  | "cpaAmount"
  | "sportsbookPnl"
  | "casinoNgr"
  | "totalOwed"
  | "totalRevenue"
  | "status";

// Order matters — drives the checkbox rendering and the saved column order.
export const SHAREABLE_COLUMN_IDS: ReadonlyArray<ShareableColumnId> = [
  "referralCode",
  "totalUsers",
  "totalFtds",
  "ftdRate",
  "cpaAmount",
  "sportsbookPnl",
  "casinoNgr",
  "totalOwed",
  "totalRevenue",
  "status",
];

export const SHAREABLE_COLUMN_LABELS: Record<ShareableColumnId, string> = {
  referralCode: AppStrings.REFERRAL_CODE,
  totalUsers: AppStrings.TOTAL_USERS,
  totalFtds: AppStrings.TOTAL_FTDS,
  ftdRate: AppStrings.FTD_RATE,
  cpaAmount: AppStrings.CPA_AMT,
  sportsbookPnl: AppStrings.SPORTSBOOK_PNL,
  casinoNgr: AppStrings.CASINO_GGR,
  totalOwed: AppStrings.TOTAL_OWED,
  totalRevenue: AppStrings.TOTAL_REVENUE,
  status: AppStrings.STATUS,
};

// `as const` keeps the literal tuple so `KpiEligibleId` narrows to the
// 5 ids rather than the full union.
export const KPI_ELIGIBLE_IDS = [
  "totalUsers",
  "totalFtds",
  "ftdRate",
  "totalRevenue",
  "totalOwed",
] as const satisfies ReadonlyArray<ShareableColumnId>;

export type KpiEligibleId = (typeof KPI_ELIGIBLE_IDS)[number];

/** Filter shareable column ids down to the KPI-eligible subset. */
export const getVisibleKpiIds = (
  selectedColumns: ReadonlyArray<ShareableColumnId>,
): KpiEligibleId[] =>
  KPI_ELIGIBLE_IDS.filter((id) =>
    selectedColumns.includes(id),
  ) as KpiEligibleId[];

// Purely UI — BE stores a flat list, these groups only drive the modal's
// two-section toggle panel. `referralCode` + `status` sit outside both.
export const PERFORMANCE_METRIC_IDS = [
  "totalUsers",
  "totalFtds",
  "ftdRate",
] as const satisfies ReadonlyArray<ShareableColumnId>;

export const FINANCIAL_METRIC_IDS = [
  "totalRevenue",
  "totalOwed",
  "cpaAmount",
  "sportsbookPnl",
  "casinoNgr",
] as const satisfies ReadonlyArray<ShareableColumnId>;

export type PerformanceMetricId = (typeof PERFORMANCE_METRIC_IDS)[number];
export type FinancialMetricId = (typeof FINANCIAL_METRIC_IDS)[number];

// Product call: the 5 sensitive metrics (CPA Amt, Sportsbook P&L, Casino
// GGR, Total Owed, Total Revenue) start OFF — operator opts the affiliate
// into seeing each one explicitly. The remaining 5 stay ON by default so
// every affiliate at minimum sees their referral code + funnel + status.
export const DEFAULT_SELECTED_COLUMNS: ReadonlyArray<ShareableColumnId> = [
  "referralCode",
  "totalUsers",
  "totalFtds",
  "ftdRate",
  "status",
];

export type AffiliatePamAccountFormValues = {
  selectedColumns: ShareableColumnId[];
};

export const AFFILIATE_PAM_ACCOUNT_INITIAL_VALUES: AffiliatePamAccountFormValues =
  {
    selectedColumns: [...DEFAULT_SELECTED_COLUMNS],
  };

export const isPamAccountValid = (
  values: Partial<AffiliatePamAccountFormValues> | undefined,
): boolean => Boolean(values?.selectedColumns?.length);
